import React, { useState } from "react";
import { formatMissingSettings } from "../../lib/utils";

// ── fetchAdminJson ─────────────────────────────────────────────────────────────

export async function fetchAdminJson(url, options = {}) {
  const isFormData = options.body instanceof FormData;
  const baseHeaders = isFormData ? {} : { "Content-Type": "application/json" };
  baseHeaders["X-Requested-With"] = "XMLHttpRequest";

  let response;
  try {
    response = await fetch(url, {
      credentials: "include",
      ...options,
      headers: { ...baseHeaders, ...(options.headers || {}) },
    });
  } catch (networkError) {
    const wrapped = new Error(
      `Network error: ${networkError.message || "Failed to fetch"}. Check your network/VPN/firewall or open DevTools → Network for the failing request.`,
    );
    wrapped.cause = networkError;
    wrapped.isNetworkError = true;
    throw wrapped;
  }

  const contentType = response.headers.get("content-type") || "";
  if (!response.ok) {
    let msg = `Error ${response.status}`;
    let payload = null;
    try {
      if (contentType.includes("application/json")) {
        payload = await response.json();
        msg = JSON.stringify(payload);
      } else {
        const text = await response.text();
        if (/<!doctype html|<html/i.test(text)) {
          msg = `Server returned HTML (status ${response.status}).`;
        } else {
          msg = text || msg;
        }
      }
    } catch {
      /* use default msg */
    }
    const error = new Error(msg);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) return null;
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    if (/<!doctype html|<html/i.test(text)) {
      throw new Error("Server returned HTML instead of JSON.");
    }
    return text ? JSON.parse(text) : null;
  }
  return response.json();
}

// ── fetchAllPages ──────────────────────────────────────────────────────────────

export async function fetchAllPages(url) {
  const results = [];
  let nextUrl = url;
  while (nextUrl) {
    const data = await fetchAdminJson(nextUrl);
    if (Array.isArray(data)) {
      return data;
    }
    (data.results || []).forEach((r) => results.push(r));
    if (data.next) {
      const parsed = new URL(data.next);
      nextUrl = parsed.pathname + parsed.search;
    } else {
      nextUrl = null;
    }
  }
  return results;
}

// ── ROLE_OPTIONS ───────────────────────────────────────────────────────────────

export const ROLE_OPTIONS = [
  { value: "agent", label: "Agent" },
  { value: "btl", label: "BTL Worker" },
  { value: "manager", label: "Manager" },
  { value: "senior_manager", label: "Senior Manager" },
  { value: "admin", label: "Admin" },
];

// ── roleLabel / roleBadgeStyle ─────────────────────────────────────────────────

export function roleLabel(role) {
  return ROLE_OPTIONS.find((option) => option.value === role)?.label || role || "User";
}

export function roleBadgeStyle(role) {
  if (role === "senior_manager") {
    return {
      background: "#f4edff",
      border: "1px solid #d9c2ff",
      color: "#6f49b6",
    };
  }

  if (role === "manager") {
    return {
      background: "#eefaf3",
      border: "1px solid #cdeedc",
      color: "#44b377",
    };
  }

  if (role === "agent") {
    return {
      background: "#edf4ff",
      border: "1px solid #cddfff",
      color: "#4b75d9",
    };
  }

  if (role === "btl") {
    return {
      background: "#fff3e0",
      border: "1px solid #ffcc80",
      color: "#e65100",
    };
  }

  return {
    background: "#fff7e9",
    border: "1px solid #ffe0a2",
    color: "#d4952f",
  };
}

// ── Hierarchy data ─────────────────────────────────────────────────────────────

export const HIERARCHY_CITY_REGION = {
  'Delhi': 'North', 'Gurugram': 'North', 'Noida': 'North', 'Ghaziabad': 'North',
  'Chandigarh': 'North', 'Lucknow': 'North', 'Jaipur': 'North',
  'Bangalore': 'South', 'Hyderabad': 'South', 'Chennai': 'South',
  'Mumbai': 'West', 'Pune': 'West',
  'Kolkata': 'East',
  'Others': 'Others',
};

export const HIERARCHY_REGION_CITIES = {
  'North': ['Delhi', 'Gurugram', 'Noida', 'Ghaziabad', 'Chandigarh', 'Lucknow', 'Jaipur'],
  'South': ['Bangalore', 'Hyderabad', 'Chennai'],
  'West': ['Mumbai', 'Pune'],
  'East': ['Kolkata'],
};

const CITY_ALIASES = {
  'bengaluru': 'Bangalore', 'bangaluru': 'Bangalore', 'banglore': 'Bangalore',
  'bengalore': 'Bangalore', 'bengalure': 'Bangalore', 'bangolore': 'Bangalore',
  'new delhi': 'Delhi', 'delhi ncr': 'Delhi', 'ncr': 'Delhi',
  'south delhi': 'Delhi', 'north delhi': 'Delhi', 'faridabad': 'Delhi',
  'gurgaon': 'Gurugram', 'gurgoan': 'Gurugram', 'gurugam': 'Gurugram',
  'greater noida': 'Noida', 'gr noida': 'Noida',
  'hyd': 'Hyderabad', 'secunderabad': 'Hyderabad', 'hydrabad': 'Hyderabad',
  'chandighar': 'Chandigarh', 'chandhigarh': 'Chandigarh',
  'lko': 'Lucknow',
  'madras': 'Chennai', 'chenai': 'Chennai', 'channai': 'Chennai',
  'bombay': 'Mumbai', 'navi mumbai': 'Mumbai', 'thane': 'Mumbai',
  'poona': 'Pune', 'pimpri': 'Pune', 'pcmc': 'Pune',
  'calcutta': 'Kolkata', 'kolkatta': 'Kolkata',
  'jaypur': 'Jaipur',
  'gaziabad': 'Ghaziabad',
};

export function normalizeCityForHierarchy(rawCity) {
  if (!rawCity) return null;
  const lower = rawCity.trim().toLowerCase();
  if (lower === 'others' || lower === 'other city' || lower === 'other cities') return 'Others';
  for (const city of Object.keys(HIERARCHY_CITY_REGION)) {
    if (city.toLowerCase() === lower) return city;
  }
  if (CITY_ALIASES[lower]) return CITY_ALIASES[lower];
  for (const [alias, city] of Object.entries(CITY_ALIASES)) {
    if (alias.length >= 3 && (lower.includes(alias) || alias.includes(lower))) return city;
  }
  for (const city of Object.keys(HIERARCHY_CITY_REGION)) {
    const cl = city.toLowerCase();
    if (cl.length >= 3 && (lower.includes(cl) || cl.includes(lower))) return city;
  }
  return null;
}

function primaryScopeForUser(user) {
  const scopes = [...(user?.geography_scopes || [])].sort((left, right) => {
    if (left.is_primary === right.is_primary) {
      return String(left.display_name || left.name || "").localeCompare(
        String(right.display_name || right.name || ""),
      );
    }

    return left.is_primary ? -1 : 1;
  });

  return scopes[0] || null;
}

export function buildHierarchyRows(users, teams) {
  const teamById = new Map((teams || []).map((team) => [String(team.id), team]));

  const regionOrder = ['North', 'South', 'East', 'West'];
  const regions = new Map(
    regionOrder.map((r) => [r, {
      id: r,
      name: r,
      seniorManagers: [],
      cities: new Map(
        (HIERARCHY_REGION_CITIES[r] || []).map((c) => [c, {
          id: c, name: c, managers: new Map(), directAgents: [],
        }])
      ),
    }])
  );

  const getOrCreateOthersBucket = () => {
    if (!regions.has('Others')) {
      regions.set('Others', {
        id: 'Others', name: 'Others', seniorManagers: [],
        cities: new Map([['Others', { id: 'Others', name: 'Others', managers: new Map(), directAgents: [] }]]),
      });
    }
    return regions.get('Others').cities.get('Others');
  };

  const getCityBucket = (rawCity) => {
    const canonical = normalizeCityForHierarchy(rawCity);
    if (!canonical) return null;
    if (canonical === 'Others') return getOrCreateOthersBucket();
    const region = HIERARCHY_CITY_REGION[canonical];
    return region ? regions.get(region)?.cities.get(canonical) || null : null;
  };

  const createPersonNode = (user, otherCityName = "") => {
    const scope = primaryScopeForUser(user);
    const team = user?.team ? teamById.get(String(user.team)) : null;
    return {
      email: user.email || "",
      fullName: user.full_name || user.username || "Unnamed user",
      id: String(user.id),
      otherCityName,
      phoneNumber: user.phone_number || "",
      role: user.role,
      scopeDisplayName: scope?.city_name || scope?.display_name || "",
      teamId: user.team ? String(user.team) : "",
      teamName: user.team_name || team?.name || "",
    };
  };

  // Returns [{bucket, rawCityName}] — rawCityName is non-empty only when the city didn't match any canonical city
  const userCityPlacements = (user) => {
    const scopes = user?.geography_scopes || [];
    const placements = scopes.map((s) => {
      const raw = (s.city_name || "").trim();
      const bucket = getCityBucket(raw);
      if (bucket) return { bucket, rawCityName: "" };
      if (raw) {
        const othersBucket = getOrCreateOthersBucket();
        return { bucket: othersBucket, rawCityName: raw };
      }
      return null;
    }).filter(Boolean);
    if (placements.length === 0) return [{ bucket: getOrCreateOthersBucket(), rawCityName: "" }];
    return placements;
  };

  (users || []).filter((u) => u.role === "senior_manager").forEach((user) => {
    const placedRegions = new Set();
    userCityPlacements(user).forEach(({ bucket, rawCityName }) => {
      const regionName = HIERARCHY_CITY_REGION[bucket.id];
      const person = createPersonNode(user, rawCityName);
      if (regionName && !placedRegions.has(regionName)) {
        placedRegions.add(regionName);
        regions.get(regionName)?.seniorManagers.push(person);
      } else if (!regionName && !placedRegions.has("Others")) {
        placedRegions.add("Others");
        bucket.directAgents.push(person);
      }
    });
    if (placedRegions.size === 0) getOrCreateOthersBucket().directAgents.push(createPersonNode(user));
  });

  (users || []).filter((u) => u.role === "manager").forEach((user) => {
    userCityPlacements(user).forEach(({ bucket, rawCityName }) => {
      if (!bucket.managers.has(user.id)) {
        bucket.managers.set(String(user.id), { ...createPersonNode(user, rawCityName), agents: [] });
      }
    });
  });

  (users || []).filter((u) => u.role === "agent").forEach((user) => {
    const team = user?.team ? teamById.get(String(user.team)) : null;
    userCityPlacements(user).forEach(({ bucket, rawCityName }) => {
      const person = createPersonNode(user, rawCityName);
      if (team?.manager) {
        const managerId = String(team.manager);
        let managerNode = bucket.managers.get(managerId);
        if (!managerNode) {
          const mUser = (users || []).find((u) => String(u.id) === managerId);
          managerNode = {
            ...(mUser ? createPersonNode(mUser) : { fullName: "Unknown Manager", id: managerId, role: "manager", otherCityName: "" }),
            agents: [],
          };
          bucket.managers.set(managerId, managerNode);
        }
        if (!managerNode.agents.find((a) => a.id === person.id)) {
          managerNode.agents.push(person);
        }
      } else {
        if (!bucket.directAgents.find((a) => a.id === person.id)) {
          bucket.directAgents.push(person);
        }
      }
    });
  });

  const sortPeople = (a, b) => a.fullName.localeCompare(b.fullName);

  return [...regions.values()]
    .map((region) => ({
      ...region,
      seniorManagers: [...region.seniorManagers].sort(sortPeople),
      cities: [...region.cities.values()]
        .filter((city) => city.managers.size > 0 || city.directAgents.length > 0)
        .map((city) => ({
          ...city,
          directAgents: [...city.directAgents].sort(sortPeople),
          managers: [...city.managers.values()]
            .map((mgr) => ({ ...mgr, agents: [...mgr.agents].sort(sortPeople) }))
            .sort(sortPeople),
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .filter((region) => region.cities.length > 0 || region.seniorManagers.length > 0);
}

// ── CSV / sample download helpers ─────────────────────────────────────────────

export async function downloadSampleTemplate(format) {
  const res = await fetch(`/api/accounts/users/sample-template/?file_format=${format}`, {
    credentials: "include",
    headers: { "X-Requested-With": "XMLHttpRequest" },
  });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `bulk-upload-users-sample.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadCsvSample(filename, headers, rows) {
  const lines = [
    headers.join(","),
    ...rows.map((row) =>
      headers.map((header) => `"${String(row[header] ?? "").replace(/"/g, '""')}"`).join(","),
    ),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function downloadPincodeSample() {
  downloadCsvSample(
    "pincode-cluster-upload-sample.csv",
    ["Pin", "Location", "Cluster", "City"],
    [
      { Pin: "560001", Location: "MG Road", Cluster: "Central Bangalore", City: "Bangalore" },
      { Pin: "122001", Location: "Sector 29", Cluster: "Central Gurgaon", City: "Gurugram" },
    ],
  );
}

export function downloadAgentAssignmentSample() {
  downloadCsvSample(
    "agent-cluster-assignment-sample.csv",
    ["Cluster", "City", "SPOC"],
    [
      { Cluster: "Central Bangalore", City: "Bangalore", SPOC: "neha.sharma@example.com" },
      { Cluster: "Central Gurgaon", City: "Gurugram", SPOC: "rohan.verma@example.com" },
    ],
  );
}

// ── CredentialsModal ───────────────────────────────────────────────────────────

export function CredentialsModal({ rows, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!rows || rows.length === 0) return null;

  function copyPassword(pw) {
    navigator.clipboard.writeText(pw).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  function downloadCsv() {
    const headers = ["Full Name", "Username", "Email", "Role", "Password"];
    const lines = [headers.join(","), ...rows.map((r) =>
      [r.full_name, r.username, r.email, r.role, r.password].map((v) => `"${(v || "").replace(/"/g, '""')}"`).join(",")
    )];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "user-credentials.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="modal-kicker">User Credentials</p>
            <h2>Save these credentials now</h2>
          </div>
          <button type="button" className="close-button" onClick={onClose}>✕</button>
        </div>

        <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
          Passwords won't be shown again. Copy or download them before closing.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 16px", fontSize: 13 }}>
                <div><span style={{ color: "var(--text-secondary)" }}>Full Name</span><br /><strong>{r.full_name || "—"}</strong></div>
                <div><span style={{ color: "var(--text-secondary)" }}>Username</span><br /><strong style={{ fontFamily: "monospace" }}>{r.username || "—"}</strong></div>
                <div><span style={{ color: "var(--text-secondary)" }}>Email</span><br /><strong>{r.email || "—"}</strong></div>
                <div><span style={{ color: "var(--text-secondary)" }}>Role</span><br /><strong>{r.role || "—"}</strong></div>
              </div>
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Password</span>
                <code style={{ flex: 1, background: "#f1f5f9", padding: "4px 10px", borderRadius: 6, fontSize: 13 }}>{r.password}</code>
                <button
                  type="button"
                  className="secondary-ghost compact-button"
                  onClick={() => copyPassword(r.password)}
                  style={{ whiteSpace: "nowrap" }}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button type="button" className="secondary-ghost" onClick={downloadCsv}>Download CSV</button>
          <button type="button" className="primary-button modal-submit" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}

// ── AdminDisclosureCard ────────────────────────────────────────────────────────

export function AdminDisclosureCard({
  title,
  description,
  actionLabel,
  open,
  onToggle,
  children,
  actions = null,
}) {
  return (
    <section className={`settings-disclosure-card ${open ? "open" : ""}`}>
      <div className="settings-disclosure-head">
        <div className="settings-disclosure-copy">
          <h3>{title}</h3>
          {description ? <p>{description}</p> : null}
        </div>
        <div className="settings-disclosure-actions">
          {actions}
          <button type="button" className="secondary-ghost settings-disclosure-toggle" onClick={onToggle}>
            {open ? "Hide" : actionLabel}
          </button>
        </div>
      </div>
      {open ? <div className="settings-disclosure-body">{children}</div> : null}
    </section>
  );
}

// ── IntegrationStatusCard ──────────────────────────────────────────────────────

export function IntegrationStatusCard({ diagnostics, title }) {
  const configuredLabel = diagnostics
    ? diagnostics.configured
      ? "Configured"
      : diagnostics.enabled
        ? "Needs setup"
        : "Disabled"
    : "Checking";
  const tone = diagnostics
    ? diagnostics.configured
      ? "green"
      : "gold"
    : "blue";
  const missingSettingsMessage = formatMissingSettings(diagnostics?.missing_settings);

  return (
    <article
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "12px",
        display: "grid",
        gap: "0.65rem",
        padding: "1rem",
      }}
    >
      <div style={{ alignItems: "center", display: "flex", justifyContent: "space-between" }}>
        <strong>{title}</strong>
        <span className={`agent-badge ${tone}`}>{configuredLabel}</span>
      </div>
      {diagnostics ? (
        <>
          {diagnostics.webhook_path ? (
            <p style={{ margin: 0 }}>
              Webhook: <span className="mono-text">{diagnostics.webhook_path}</span>
            </p>
          ) : null}
          {diagnostics.dial_path ? (
            <p style={{ margin: 0 }}>
              Dial path: <span className="mono-text">{diagnostics.dial_path}</span>
            </p>
          ) : null}
          {diagnostics.auth_mode ? (
            <p style={{ margin: 0 }}>
              Auth mode: <span className="mono-text">{diagnostics.auth_mode}</span>
            </p>
          ) : null}
          {diagnostics.graph_api_version ? (
            <p style={{ margin: 0 }}>
              Graph API: <span className="mono-text">{diagnostics.graph_api_version}</span>
            </p>
          ) : null}
          {missingSettingsMessage ? (
            <p className="helper-text" style={{ margin: 0 }}>
              {missingSettingsMessage}
            </p>
          ) : (
            <p className="helper-text" style={{ margin: 0 }}>
              No missing environment keys reported.
            </p>
          )}
        </>
      ) : (
        <p className="helper-text" style={{ margin: 0 }}>
          Loading integration diagnostics.
        </p>
      )}
    </article>
  );
}
